import React, { useEffect, useState } from "react";
import axios from "../api/axiosInstance";
import {
  FaEdit,
  FaSearch,
  FaHeart,
  FaCheckCircle,
  FaTimesCircle,
  FaUserCircle,
} from "react-icons/fa";

export default function SuccessStoryTable() {
  const [stories, setStories] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(
    () => parseInt(localStorage.getItem("pageSize")) || 10,
  );
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [selectedStory, setSelectedStory] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchStories();
  }, [filterText, page, pageSize]);

  const fetchStories = async () => {
    setLoading(true);
    try {
      const params = { search: filterText, page, page_size: pageSize };
      const res = await axios.get("/api/success-stories-admin/", { params });

      if (res.data.results) {
        setStories(res.data.results);
        setCount(res.data.count);
      } else {
        setStories(res.data);
        setCount(res.data.length);
      }
    } catch (err) {
      console.error("Error fetching stories:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (story) => {
    setSelectedStory(story);
    setApprovalStatus(story.is_approved ? 1 : 0);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!selectedStory) return;
    setSaving(true);
    try {
      await axios.put(`/api/success-stories/${selectedStory.id}/`, {
        ...selectedStory,
        is_approved: approvalStatus,
      });
      setShowModal(false);
      fetchStories();
    } catch (err) {
      console.error("Error updating story:", err);
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.ceil(count / pageSize) || 1;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const getPageNumbers = () => {
    const pages = [];
    const delta = 1;
    const range = [];
    for (
      let i = Math.max(2, page - delta);
      i <= Math.min(totalPages - 1, page + delta);
      i++
    ) {
      range.push(i);
    }
    if (page - delta > 2) range.unshift("ellipsis-start");
    if (page + delta < totalPages - 1) range.push("ellipsis-end");
    pages.push(1);
    pages.push(...range);
    if (totalPages > 1) pages.push(totalPages);
    return [...new Set(pages)];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 px-3 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-rose-500 text-white shadow-sm">
            <FaHeart size={15} />
          </span>
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
              Success Stories
            </h1>
            <p className="text-sm text-slate-500">
              {count} {count === 1 ? "story" : "stories"} submitted
            </p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-sm">
            <FaSearch
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              size={14}
            />
            <input
              type="text"
              placeholder="Search by name, email or matrimony ID..."
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-4 text-sm text-slate-800 placeholder:text-slate-400 transition focus:border-rose-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-rose-100"
              value={filterText}
              onChange={(e) => {
                setPage(1);
                setFilterText(e.target.value);
              }}
            />
          </div>

          <div className="flex items-center justify-between gap-2 sm:justify-end">
            <label className="whitespace-nowrap text-sm font-medium text-slate-600">
              Rows per page
            </label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPage(1);
                setPageSize(Number(e.target.value));
                localStorage.setItem("pageSize", e.target.value);
              }}
              className="cursor-pointer rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700 transition focus:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-100"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        {/* ===== Desktop / tablet table ===== */}
        <div className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm xl:block">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/80 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3.5 w-10">#</th>
                  <th className="px-4 py-3.5">Photo</th>
                  <th className="px-4 py-3.5">Couple</th>
                  <th className="px-4 py-3.5">Matrimony IDs</th>
                  <th className="px-4 py-3.5">Email</th>
                  <th className="px-4 py-3.5">Marriage Date</th>
                  <th className="px-4 py-3.5">Story</th>
                  <th className="px-4 py-3.5">Status</th>
                  <th className="px-4 py-3.5 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-4 py-12 text-center text-slate-400"
                    >
                      <span className="inline-flex items-center gap-2 text-sm">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-rose-500" />
                        Loading stories...
                      </span>
                    </td>
                  </tr>
                ) : stories.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-14 text-center">
                      <div className="flex flex-col items-center gap-2 text-slate-400">
                        <FaHeart size={26} />
                        <p className="text-sm font-medium text-slate-500">
                          No records found
                        </p>
                        <p className="text-xs">Try adjusting your search</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  stories.map((s, index) => (
                    <tr
                      key={s.id}
                      className="group transition-colors hover:bg-rose-50/40"
                    >
                      <td className="px-4 py-3.5 align-top text-slate-400">
                        {(page - 1) * pageSize + index + 1}
                      </td>
                      <td className="px-4 py-3.5 align-top">
                        {s.photo ? (
                          <img
                            src={s.photo}
                            alt={`${s.bride_name} and ${s.groom_name}`}
                            className="h-12 w-12 rounded-full border border-slate-200 object-cover"
                          />
                        ) : (
                          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-300">
                            <FaUserCircle size={22} />
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 align-top">
                        <p className="font-medium text-slate-800">
                          {s.bride_name}
                          <FaHeart
                            size={9}
                            className="mx-1.5 inline text-rose-400"
                          />
                          {s.groom_name}
                        </p>
                      </td>
                      <td className="px-4 py-3.5 align-top text-slate-600">
                        <div className="flex flex-col text-xs">
                          <span>
                            <span className="text-slate-400">User: </span>
                            {s.user_matrimony_id}
                          </span>
                          <span>
                            <span className="text-slate-400">Partner: </span>
                            {s.partner_matrimony_id || "-"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 align-top text-slate-600">
                        {s.email}
                      </td>
                      <td className="px-4 py-3.5 align-top text-slate-600">
                        {formatDate(s.marriage_date)}
                      </td>
                      <td className="px-4 py-3.5 align-top">
                        <p className="max-w-[220px] text-slate-600 line-clamp-2">
                          {s.story}
                        </p>
                      </td>
                      <td className="px-4 py-3.5 align-top">
                        {s.is_approved ? (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                            <FaCheckCircle size={11} />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500">
                            <FaTimesCircle size={11} />
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-center align-top">
                        <button
                          onClick={() => handleEdit(s)}
                          aria-label={`Edit story for ${s.bride_name} and ${s.groom_name}`}
                          className="rounded-full p-2.5 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
                        >
                          <FaEdit size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ===== Cards (below xl breakpoint, including tablets) ===== */}
        <div className="space-y-3 xl:hidden">
          {loading ? (
            <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white py-12 text-slate-400 shadow-sm">
              <span className="inline-flex items-center gap-2 text-sm">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-rose-500" />
                Loading stories...
              </span>
            </div>
          ) : stories.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-white py-14 text-slate-400 shadow-sm">
              <FaHeart size={26} />
              <p className="text-sm font-medium text-slate-500">
                No records found
              </p>
              <p className="text-xs">Try adjusting your search</p>
            </div>
          ) : (
            stories.map((s) => (
              <div
                key={s.id}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {s.photo ? (
                      <img
                        src={s.photo}
                        alt={`${s.bride_name} and ${s.groom_name}`}
                        className="h-14 w-14 shrink-0 rounded-full border border-slate-200 object-cover"
                      />
                    ) : (
                      <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-300">
                        <FaUserCircle size={26} />
                      </span>
                    )}
                    <div>
                      <p className="font-medium text-slate-800">
                        {s.bride_name}
                        <FaHeart
                          size={9}
                          className="mx-1.5 inline text-rose-400"
                        />
                        {s.groom_name}
                      </p>
                      <p className="text-xs text-slate-400">
                        Married {formatDate(s.marriage_date)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleEdit(s)}
                    aria-label={`Edit story for ${s.bride_name} and ${s.groom_name}`}
                    className="shrink-0 rounded-full p-2.5 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
                  >
                    <FaEdit size={14} />
                  </button>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3 text-xs text-slate-600">
                  <p>
                    <span className="text-slate-400">User ID: </span>
                    {s.user_matrimony_id}
                  </p>
                  <p>
                    <span className="text-slate-400">Partner ID: </span>
                    {s.partner_matrimony_id || "-"}
                  </p>
                  <p className="col-span-2 truncate">
                    <span className="text-slate-400">Email: </span>
                    {s.email}
                  </p>
                </div>

                {s.story && (
                  <p className="mt-3 rounded-xl bg-slate-50 p-3 text-sm leading-relaxed text-slate-600 line-clamp-3">
                    {s.story}
                  </p>
                )}

                <div className="mt-3">
                  {s.is_approved ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                      <FaCheckCircle size={11} />
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500">
                      <FaTimesCircle size={11} />
                      Inactive
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {!loading && stories.length > 0 && (
          <div className="mt-5 flex flex-col items-center justify-between gap-3 sm:flex-row">
            <p className="text-sm text-slate-500">
              Page <span className="font-medium text-slate-700">{page}</span> of{" "}
              <span className="font-medium text-slate-700">{totalPages}</span>
            </p>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Prev
              </button>

              <div className="flex items-center gap-1">
                {getPageNumbers().map((p, i) =>
                  typeof p === "string" ? (
                    <span
                      key={p + i}
                      className="px-1.5 text-sm text-slate-400 select-none"
                    >
                      &hellip;
                    </span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => handlePageChange(p)}
                      className={`min-w-[2.25rem] rounded-lg px-2.5 py-1.5 text-sm font-medium transition ${
                        page === p
                          ? "bg-rose-500 text-white shadow-sm"
                          : "text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      {p}
                    </button>
                  ),
                )}
              </div>

              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && selectedStory && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4"
          onClick={() => !saving && setShowModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
          >
            <div className="mb-1 flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-100 text-rose-600">
                <FaHeart size={14} />
              </span>
              <h2 className="text-lg font-semibold text-slate-900">
                Update Approval Status
              </h2>
            </div>
            <p className="mb-5 text-sm text-slate-500">
              {selectedStory.bride_name} &amp; {selectedStory.groom_name}
            </p>

            <div className="mb-6 grid grid-cols-2 gap-3">
              <label
                className={`flex cursor-pointer items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium transition ${
                  approvalStatus === 0
                    ? "border-slate-400 bg-slate-50 text-slate-700"
                    : "border-slate-200 text-slate-500 hover:bg-slate-50"
                }`}
              >
                <input
                  type="radio"
                  value={0}
                  checked={approvalStatus === 0}
                  onChange={() => setApprovalStatus(0)}
                  className="hidden"
                />
                <FaTimesCircle size={13} />
                Inactive
              </label>

              <label
                className={`flex cursor-pointer items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium transition ${
                  approvalStatus === 1
                    ? "border-emerald-400 bg-emerald-50 text-emerald-700"
                    : "border-slate-200 text-slate-500 hover:bg-slate-50"
                }`}
              >
                <input
                  type="radio"
                  value={1}
                  checked={approvalStatus === 1}
                  onChange={() => setApprovalStatus(1)}
                  className="hidden"
                />
                <FaCheckCircle size={13} />
                Active
              </label>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowModal(false)}
                disabled={saving}
                className="rounded-xl px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 rounded-xl bg-rose-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving && (
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                )}
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
